import { DropdownMenu, useToast, SidebarToggle, matchesExactKey, createPlatformShortcut, Icon } from "@aidkitorg/component-library";
import { QuickJS } from "@aidkitorg/types/lib/quickJS";
import { enableKeyIsValid } from "@aidkitorg/types/lib/eval";
import { Dashboard, ExpandedSurvey, ForceSourceMap, NotificationGroup, Root, Subsurvey, Survey, TargetFieldRef, Text } from "@aidkitorg/types/lib/survey";
import { CollectScopeForUser, EmailToUserId } from "@aidkitorg/types/lib/translation/permissions";
import { DependencyMap, DependencyNode, TRANSLATORS } from "@aidkitorg/types/lib/translation/questions";
import { expandTemplates, expandTemplatesStrict } from "@aidkitorg/types/lib/translation/v0_to_legacy";
import { ChangeSet, State, UserActivity } from "@aidkitorg/typesheets/lib/collab";
import { ExtraJsxActions, ExtraJsx } from "@aidkitorg/typesheets/lib/editor";
import Cytoscape from 'cytoscape';
import React, { MouseEvent, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { get_deployment, usePost, useRoboScreener } from "./API";
import { useCollabActions } from "./CollabWrapper";
import { Dropdown } from "./Components/Dropdown";
import RoboNavConsole from "./Components/RoboNavConsole";
import AutomaticMessageHandler from "./Config/AMH";
import { Changes } from "./Config/Changes";
import CoachTab from "./Config/CoachTab";
import { NotificationsView } from "./Config/Notifications";
import PaymentsEditor from "./Config/Payments";
import SurveyPreview from "./Config/SurveyPreview";
import TestCases from "./Config/TestCases";
import enumerateSubsurveys from "./Config/Util";
import ConfigSearch, { buildFieldMismatchPathMap, FieldMismatchMap, findAndGoToComponent, highlightSearchTerm } from "./ConfigSearch";
import { ContentTemplateModal } from "./ContentTemplateModal";
import InterfaceContext, { ConfigurationContext, PublicConfigurationContext, ScrollToTopContext, UserInfoContext } from "./Context";
import { ChatConversationProvider } from "./Contexts/ChatContext";
import { DistroDashboard } from "./DistroDashboard";
import { StartReviewConflictModal } from "./Config/StartReviewConflictModal";
import { useDebouncedMemo } from "./Hooks/Debounce";
import { supportedLanguages, useLocalizedStrings } from "./Localization";
import { MACROS } from "./macros/Macros";
import { MIGRATIONS } from "./Migrations";
import { InfoDict } from "./Questions/Props";
import { FacePile } from "./Realtime";
import { RoboscreenerSurveyErrorsModal } from "./RoboscreenerSurveyErrorsModal";
import { TargetFieldExplorer } from "./TargetFieldExplorer";
import { TranslationModal } from "./TranslationModal";
import { generateRandomString, getDistroBrowserIncompatibility, removeDuplicateEvents, safeParse, SpacedSpinner, useInterval } from "./Util";
import { getDependencyMap, findAnyDownstreamNotifications } from "@aidkitorg/types/lib/translation/dependencyMap";
import type { ReviewRequiredResult } from "@aidkitorg/aidkit/lib/distro/reviewRequired";
import { ReviewRequiredModal } from "./Config/ReviewRequiredModal";
import { useMultipartUpload } from "./utils/useMultipartUpload";
import { ResetProgramModal } from "./ResetProgramModal";
import { DefaultChatSessionProvider } from "./Contexts/ChatSessionContext";
import { DistroValidation, DistroValidationModal } from "./Config/DistroValidation";
import { validateDistro, ValidationResult } from "@aidkitorg/types/lib/distroValidator/distroValidator";
import { captureException } from "@sentry/react";

const elk = require('cytoscape-elk');
Cytoscape.use(elk);

function extractDedupedTranslations(root: any, stack: Set<string> = new Set()): { en: string, [key: string]: string }[] {
  if (typeof root.en === "string") {
    if (!root._id || stack.has(root._id)) {
      root._id = uuidv4();
    }
    stack.add(root._id);
    return [{ ...root }];
  }
  if (Array.isArray(root)) {
    return root.flatMap(i => extractDedupedTranslations(i, stack));
  }
  if (typeof root === "object") {
    return Object.values(root).flatMap(i => extractDedupedTranslations(i, stack));
  }
  return [];
}

function importTranslations(root: any, strings: { _id?: string, [key: string]: string | undefined }[]) {
  if (typeof root.en === "string" && root._id) {
    let translation = strings.find((s) => s._id === root._id);
    if (translation) {
      Object.keys(translation).forEach((key) => {
        if (key !== '_id' && translation![key]) {
          root[key] = translation![key];
        }
      });
    }
  }
  if (Array.isArray(root)) {
    root.flatMap((r) => importTranslations(r, strings));
  }
  if (typeof root === "object") {
    Object.values(root).flatMap((r) => importTranslations(r, strings));
  }
}

function DashboardDetail(props: { dashboards: Dashboard[] }) {
  if (!props.dashboards?.length) {
    return <div className="mt-4 ml-1">No dashboards yet!</div>
  }
  return <>{props.dashboards.map((dashboard => DistroDashboard({ dashboard: dashboard.path, isEmbedded: true })))}</>
}

export const DistroContext = React.createContext<DependencyMap>(new Map<TargetFieldRef, DependencyNode>());

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));

export default function ConfigPage(props?: { macroEditor?: boolean }) {
  const params = useParams<{ survey: string }>();
  const [survey, setSurvey] = useState<Root | null>(null);
  const surveyString = useDebouncedMemo(() => survey ? JSON.stringify(survey) : null, 500, [survey]);

  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);
  const stateRef = useRef<State | undefined>(undefined);

  const { personas } = survey as ExpandedSurvey || {};
  const L = useLocalizedStrings();
  const history = useHistory();

  const user = useContext(UserInfoContext);
  const publicConfiguration = useContext(PublicConfigurationContext);
  const [tabId, setTabId] = useState(generateRandomString(4, 'abcdefghjkmnpqrstuvwxyz123456789'));
  const { toast } = useToast();

  const [mockInfo, setMockInfo] = useState<InfoDict>({});

  const getDistroConfigName = () => {
    return params.survey || window.location.hash.slice(1);
  }

  const [distroConfigName, setDistroConfigName] = useState(getDistroConfigName());
  const [loadedHash, setLoadedHash] = useState("");
  const [latestHash, setLatestHash] = useState("");
  /** Whether the survey has changed since the latest active survey, or the base-commit if applicable */
  const surveyChangedSinceLoaded = useMemo(() => {
    return !!(loadedHash && latestHash && loadedHash !== latestHash);
  }, [loadedHash, latestHash]);
  const [latestSurvey, setLatestSurvey] = useState<Survey | ExpandedSurvey | null>(null);
  const latestSurveyString = useDebouncedMemo(() => latestSurvey ? JSON.stringify(latestSurvey) : null, 500, [latestSurvey]);
  /** Whether the survey has changed since the latest active survey */
  const surveyChangedSinceLatest = useMemo(() => {
    return latestSurveyString !== '{}' && surveyString !== '{}' && latestSurveyString !== surveyString;
  }, [latestSurveyString, surveyString]);
  const [showBreadcrumb, setShowBreadcrumb] = useState(true);
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [translations, setTranslations] = useState<Text[]>([]);
  const [translationModalOpen, setTranslationModalOpen] = useState(false);
  const [contentTemplateModalOpen, setContentTemplateModalOpen] = useState(false);
  const [startReviewConflictModalOpen, setStartReviewConflictModalOpen] = useState(false);
  const initialTranslations = useRef('');
  const interfaceContext = useContext(InterfaceContext);

  const [surveys, setSurveys] = useState<Awaited<ReturnType<typeof listSurveys>> | null>(null);
  const loadingSurveys = useRef(false);
  const [surveyName, setSurveyName] = useState("");
  const [activeSurvey, setActiveSurvey] = useState<string | null | undefined>(window.location.hash.slice(1) || undefined);  
  const [commitId, setCommitId] = useState<string | null>(new URLSearchParams(window.location.search).get('commit') || null);
  const [latestCommitMetadata, setLatestCommitMetadata] = useState<{ id: string, reviewId: string, reviewStatus: string } | null>(null);
  
  const [selectedPreview, setSelectedPreview] = useState<'survey' | 'notifications' | 'payments' | 'targetFields' | 'dashboards' | 'testCases' | 'changes' | 'amh' | 'fieldMismatches' | 'robonav' | 'ai-chat' | 'validationSummary'>('survey');
  const config = useContext(ConfigurationContext);
  const [subsurveys, setSubsurveys] = useState(null as null | Subsurvey[]);

  const [roboscreenerValidationErrors, setRoboscreenerValidationErrors] = useState<(string | { hint: string, error: string })[]>();
  const [distroValidationErrorsForModal, setDistroValidationErrorsForModal] = useState<ValidationResult[] | null>(null);
  const [reviewRequired, setReviewRequired] = useState<ReviewRequiredResult | null>(null);

  const loadSurvey = usePost('/survey/load_survey');
  const loadSurveyHash = usePost('/survey/load_survey_metadata');
  const saveSurvey = usePost('/survey/save_survey', { compressRequestPayload: true });
  const doMagic = usePost('/program/admin/magicV2');
  const doesSurveyExist = usePost('/survey/does_survey_exist');
  const sendNotifications = usePost("/program/admin/force_notifications_immediately");
  const validateSurveyRS = useRoboScreener("/check_survey_validity", { compressRequestPayload: true });

  const getEverything = usePost('/applicant/get_everything');
  const listSurveys = usePost('/survey/list_surveys');
  const startDistroReviewReq = usePost('/distro-review/start', { compressRequestPayload: true });
  const getDistroCommit = usePost('/distro-review/commit/get');
  const getLatestDistroCommitMetadata = usePost('/distro-review/commit/get-latest', { suppressErrorDisplay: true });
  const getMacroRequest = usePost('/survey/get_macro');

  const saveCollabDynamoEvents = usePost('/survey/save_collab_dynamo_events');

  const multipartUpload = useMultipartUpload();

  useEffect(() => {
    window.location.hash = activeSurvey ? '#' + activeSurvey : '';
  }, [activeSurvey]);

  async function getMacro(macroConfig: any): Promise<any> {
    const getMacroResult = await getMacroRequest({ macroConfig });
    const macroGenerator = safeParse(getMacroResult.macro!).survey
    async function evaluateIncludeIf(condition: string, macroConfig: any): Promise<boolean> {
      const result = await QuickJS.compute(condition, { macroConfig });
      return result instanceof Error ? false : result;
    }

    async function applyUpdate(component: any, updateCode: string, macroConfig: any): Promise<any> {
      const updatedComponent = await QuickJS.compute(updateCode, { component, macroConfig });
      if (updatedComponent instanceof Error) {
        captureException(new Error("Error applying update from macro"), { extra: { updateCode, component, macroConfig } });
        return '';
      }
      return updatedComponent;
    }

    async function traverseAndExpand(data: any, macroConfig: any): Promise<any> {
      // TODO(fay): handle nested Macros so we can combine them into a single parent macro
      if (Array.isArray(data)) {
        const updatedComponents = [];

        for (const component of data) {
          let updatedComponent = component;

          if (component.macroIncludeIf) {
            if (!(await evaluateIncludeIf(component.macroIncludeIf, macroConfig))) {
              continue;
            }
            delete updatedComponent.macroIncludeIf;
          }

          if (component.macroUpdate) {
            updatedComponent = await applyUpdate(updatedComponent, component.macroUpdate, macroConfig);
            delete updatedComponent.macroUpdate;
          }

          if (updatedComponent && typeof updatedComponent === 'object') {
            updatedComponent = await traverseAndExpand(updatedComponent, macroConfig);
          }

          if (updatedComponent) updatedComponents.push(updatedComponent);
        }

        return updatedComponents;

      } else if (data && typeof data === 'object') {
        const updatedData: any = {};

        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            updatedData[key] = await traverseAndExpand(data[key], macroConfig);
          }
        }

        return updatedData;
      }

      return data;
    }

    return traverseAndExpand(macroGenerator, macroConfig.type);
  }

  // State management & actions for Collab Mode 
  const {
    // if collab is not enables, this hook doesn't do much
    collabEnabled,
    // false if collab is actively receiving and merging events, otherwise true
    canPublish,
    // tell listening clients that you just published
    alertJustPublished,
    // refresh collab events and settings
    getCollabState,
    // to be called whenever you make a distro change - this handles sending events in realtime 
    // and also persisting new events to dynamo / s3
    handleNewCollabEvents,
    // updates state var and sets up base events to begin using collab mode.
    // also triggers a refresh on other clients listening browsers.
    enableCollab,
    // updates state var and persisted collab settings.
    // also triggers a refresh on other clients listening browsers.
    disableCollab,
    // uploads large amounts of events and saves a new version, with those events 
    // merged into the exising ops.
    writeNewCollabVersion
  } = useCollabActions(
    distroConfigName,
    tabId,
    (events: ChangeSet, userActivity?: UserActivity[], initialize?: boolean, debug?: boolean, unpublishedEvents?: ChangeSet) => {
      if (initialize) {
        distroRef.current?.initialize(survey,
          { ...user, name: config?.user?.name, tabId },
          events,
          userActivity,
          undefined,
          undefined,
          unpublishedEvents,
          { enableKeyIsValid, debug }
        );
      } else {
        distroRef.current?.mergeEvents(events, userActivity, unpublishedEvents);
      }
    });

  const [publishing, setPublishing] = useState(false);
  const [enablingCollab, setEnablingCollab] = useState(false);
  const [lastPublished, setLastPublished] = useState(null as null | string);
  const lastActivityTime = useRef(Date.now());

  // Tracks incidents of referenced target field variables in text
  // not aligning across different languages to help catch misconfigurations
  const [fieldMismatches, setFieldMismatches] = useState<FieldMismatchMap>({});

  const handleUpdatesRef = useRef<typeof handleUpdatesInner | null>(null);
  const handleUpdatesInner = (survey: Root, state: State, events: ChangeSet, expanded: Root) => {
    if (events.length && collabEnabled) {
      handleNewCollabEvents(events, state.myActivity, state);
    }

    stateRef.current = state;

    if (expanded) {
      setSurvey(expanded);
    } else {
      setSurvey(survey);
    }
  }

  handleUpdatesRef.current = handleUpdatesInner;

  // Gross hack because distro doesn't pick up changes
  const handleUpdates = (survey: Root, state: State, events: ChangeSet, expanded: Root) => {
    if (handleUpdatesRef.current) {
      handleUpdatesRef.current(survey, state, events, expanded);
    }
  }

  useEffect(() => {
    if (surveys || loadingSurveys.current) return;
    loadingSurveys.current = true;
    (async () => {
      setSurveys(await listSurveys({}));
      loadingSurveys.current = false;
    })();
  }, []);

  useEffect(() => {
    const storedShowRightPanel = localStorage.getItem('showRightPanel');
    if (storedShowRightPanel) {
      setShowRightPanel(JSON.parse(storedShowRightPanel));
    }
    const storedShowBreadcrumb = localStorage.getItem('showBreadcrumb');
    if (storedShowBreadcrumb) {
      setShowBreadcrumb(JSON.parse(storedShowBreadcrumb));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('showRightPanel', JSON.stringify(showRightPanel));
  }, [showRightPanel]);

  useEffect(() => {
    localStorage.setItem('showBreadcrumb', JSON.stringify(showBreadcrumb));
  }, [showBreadcrumb]);

  async function initialLoad(distroConfigName?: string) {
    let distroConfigToLoad = distroConfigName;
    if (!distroConfigToLoad) {
      const enteredDistroConfigName = prompt('Enter a name to load');
      if (enteredDistroConfigName) {
        distroConfigToLoad = enteredDistroConfigName;
        setDistroConfigName(enteredDistroConfigName);
      }
    }
    if (!distroConfigToLoad) {
      return;
    }
    ['config', 'macro-editor'].forEach(path => {
      if (window.location.pathname.startsWith(`/${path}`) && !window.location.pathname.startsWith(`/${path}/`)) {
        window.location.pathname = `/${path}/${distroConfigToLoad}`;
      }
    });
    
    let surveyResp: { config: Root | ExpandedSurvey; hash: string; lastModified: string; references?: any };
    let survey: Root | ExpandedSurvey;
    
    const latestCommitMetadataResp = await getLatestDistroCommitMetadata({ surveyName: distroConfigToLoad });
    if (latestCommitMetadataResp && !('error' in latestCommitMetadataResp)) {
      setLatestCommitMetadata(latestCommitMetadataResp);
    }
    
    if (commitId) {
      const commitSurveyResp = await getDistroCommit({ id: commitId });

      if (!commitSurveyResp || commitSurveyResp.error || !commitSurveyResp.survey) {
        toast({
          description: commitSurveyResp?.error || 'Error loading commit survey',
          variant: 'error'
        });
        return;
      }
      if (commitSurveyResp.surveyName !== distroConfigToLoad) {
        toast({
          description: 'The commit you are trying to load is for a different survey.',
          variant: 'error'
        });
        return;
      }
      
      survey = commitSurveyResp.survey;
      // For commit surveys, we don't have hash/lastModified, so set defaults
      surveyResp = {
        config: survey,
        hash: commitSurveyResp.parentHash || '',
        lastModified: '',
        references: undefined
      };
    } else {
      // Use regular survey endpoint
      setCommitId(null);
      surveyResp = await loadSurvey({ name: distroConfigToLoad, purpose: "load function called" });
      
      if (!surveyResp) {
        toast({
          description: 'Error loading survey',
          variant: 'error'
        });
        return;
      }
      
      survey = surveyResp.config;
    }

    setFieldMismatches(buildFieldMismatchPathMap((expandTemplatesStrict(survey) as ExpandedSurvey).survey));

    setLoadedHash(surveyResp.hash);
    setLatestHash(surveyResp.hash);
    setLastPublished(surveyResp.lastModified);
    setLatestSurvey(survey);
    // Make sure survey has personas property
    if (!Array.isArray(survey) && !survey?.personas) {
      survey = { ...survey, personas: [] };
    }

    const collabResp = await getCollabState();

    function tryLoad() {
      if (distroRef.current) {
        const userActivity = Object.keys(collabResp.userActivity || {})
          .map(k => {
            return {
              lastEditedObject: collabResp.userActivity[k].lastEditedObject,
              uid: collabResp.userActivity[k].uid,
              name: collabResp.userActivity[k].name,
              tabId: collabResp.userActivity[k].tabId
            }
          });

        distroRef.current?.initialize(survey,
          { ...user, name: config?.user?.name, tabId },
          collabResp.collabEnabled ? collabResp.events : undefined,
          collabResp.collabEnabled ? userActivity : undefined,
          surveyResp.references,
          undefined,
          collabResp.collabEnabled ? collabResp.unpublishedEvents : undefined,
          { enableKeyIsValid, debug: !!collabResp.debug }
        );
      } else {
        setTimeout(tryLoad, 200);
      }
    }
    tryLoad();
    //setSurvey(survey);
    setSurveyName(distroConfigToLoad);
  }

  const reloadSurvey = useCallback(async (syncEvents?: boolean) => {
    if (!surveyName) return;

    // In collab mode, we do not need to sync persisted state as often since we are passing 
    // state back and forth via websockets. Only if we haven't had activity in the last 3 minutes 
    // should we sync to keep everything up to date.
    const currentTime = Date.now();
    const inactivityDuration = currentTime - lastActivityTime.current;
    const doSync = syncEvents || (inactivityDuration > 3 * 60 * 1000);

    const latestCommit = await getLatestDistroCommitMetadata({ surveyName: surveyName });
    if (latestCommit && !('error' in latestCommit)) {
      setLatestCommitMetadata(latestCommit);
    }
    const metadataResponse = await loadSurveyHash({ name: surveyName, purpose: "reloadSurvey called" });
    if (metadataResponse && (metadataResponse.hash !== latestHash || metadataResponse.lastModified !== lastPublished)) {
      const surveyResp = await loadSurvey({ name: surveyName, purpose: "reloadSurvey called" });

      if (surveyResp) {
        setLatestHash(surveyResp.hash);
        setLastPublished(surveyResp.lastModified);
        setLatestSurvey(surveyResp.config);
      }

      // causes survey to totally reload, disrupting anyone using it.
      // if (doSync) {
      //   distroRef.current?.initialize(surveyResp.config, { ...user, name: config?.user?.name, tabId });
      // }
      
    }

    if (collabEnabled && doSync) {
      const collabResp = await getCollabState();
      distroRef.current?.mergeEvents(collabResp.events);
      lastActivityTime.current = Date.now();
    }
  }, [lastActivityTime, surveyName, collabEnabled, latestHash, lastPublished]);
  useInterval(reloadSurvey, 3000);

  useEffect(() => {
    const handleVisibilityChange = () => {
      // if we are just coming back, reload to make sure we didn't miss anything.
      if (!document.hidden && collabEnabled) {
        reloadSurvey(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleActivity = () => {
      lastActivityTime.current = Date.now();
    };

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [collabEnabled]);

  useEffect(() => {
    if (distroConfigName) {
      initialLoad(distroConfigName);
    }
  }, [user, distroConfigName]);

  // Remove commit query parameter from URL after it's been consumed
  useEffect(() => {
    if (commitId && window.location.search.includes('commit=')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('commit');
      window.history.replaceState({}, '', url.toString());
    }
  }, [commitId]);

  // This makes the bundler include the file that is otherwise just types
  ForceSourceMap();

  useEffect(() => {
    if (!survey) return;
    const localSubsurveys = enumerateSubsurveys(survey);
    setSubsurveys(localSubsurveys);

  }, [survey]);


  // If the active survey is not in the available subsurveys, set it to null
  // Note this MUST be below the initialLoad call or we won't
  // have backwards compatibility with /config#entireprogram
  useEffect(() => {
    if (subsurveys?.length && activeSurvey) {
      const found = subsurveys?.find(s => s.path === activeSurvey);
      if (!found) {
        setActiveSurvey(null);
      }
    }
  }, [subsurveys, activeSurvey]);

  async function doEnableCollab() {
    if (confirm("This is a new experimental feature. It may not work well, we don't recomend it for surveys/etc people that are live. Continue?")) {
      setEnablingCollab(true);
      const toSend = JSON.stringify(distroRef.current!.getPrunedEvents(stateRef.current!));
      await enableCollab(toSend);
      setEnablingCollab(false);
    }
  }

  async function doDisableCollab() {
    if (confirm("Note that if you haven't published/saved recently this may revert the world to a surprising state. Continue?")) {
      await disableCollab();
    }
  }

  const publish = useCallback(async ({skipDistroValidation = false}: {skipDistroValidation?: boolean} = {}) => {
    if (!canPublish || !survey) return;
    if (!skipDistroValidation) {
      const distroValidationResult = validateDistro(survey);
      if (distroValidationResult.length > 0) {
        if (distroValidationResult.some(result => result.severity === 'error')) {
          setDistroValidationErrorsForModal(distroValidationResult);
          setPublishing(false);
          return;
        } else {
          toast({
            description: <div>
              Publishing with warnings,
              please check the <button className="text-blue-500 underline cursor-pointer" onClick={() => setSelectedPreview("validationSummary")}>Validation panel</button>.
            </div>,
            variant: 'default',
            icon: <Icon name="ExclamationTriangleIcon" type="solid" />
          })
        }
      }
    }
    const surveyStringForPublish = JSON.stringify(survey);

    if (collabEnabled && !confirm("Collab is enabled. Have you checked with your collaborators before publishing?")) {
      return;
    }

    let distroConfigToSave = distroConfigName;
    if (!distroConfigToSave) {
      const newlyEnteredName = prompt('Enter a name to save');
      if (newlyEnteredName) {
        distroConfigToSave = newlyEnteredName;
        setDistroConfigName(newlyEnteredName);
      }
    }
    if (!distroConfigToSave) {
      return;
    }
    // Ask user for a commit message to be displayed on the versions page.
    const commitMessage = prompt("Please enter a version description (Optional)");
    if (commitMessage === null) {
      return;
    }
    setPublishing(true);
    try {
      const roboscreenerValidationResponse = await validateSurveyRS({ survey: surveyStringForPublish });

      if (!roboscreenerValidationResponse || 'error' in roboscreenerValidationResponse) {
        setRoboscreenerValidationErrors([roboscreenerValidationResponse.error || 'An error occurred']);
        setPublishing(false);
        return;
      } else if (roboscreenerValidationResponse.value.errors) {
        setRoboscreenerValidationErrors(roboscreenerValidationResponse.value.errors);
        setPublishing(false);
        return;
      }
    } catch (e) {
      console.log(e);
      setPublishing(false);
      return;
    }

    let newUnpublishedEvents: ChangeSet = [];

    if (!collabEnabled) {

      // Check if the live survey has changed in remote since it was loaded
      let surveyHashResp: { hash: string, lastModified: string } | undefined;
      try {
        surveyHashResp = await loadSurveyHash({ name: distroConfigToSave, purpose: "checking survey has not changed" });
        if (loadedHash && surveyHashResp.hash !== loadedHash) {
          const confirmOverwrite = confirm("The survey has changed since you first loaded it, do you want to overwrite it?");
          if (!confirmOverwrite) {
            setPublishing(false);
            return;
          }
        }
      } catch (e) {
      // It's only best effort;
      }

      // Check for local updates before saving to reduce clutter in versions page
      const currentHash = await (async () => {
        const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(surveyStringForPublish));
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      })();
      if (surveyHashResp?.hash !== currentHash) {
        const response = await saveSurvey({
          name: distroConfigToSave,
          content: surveyStringForPublish,
          browserTab: tabId,
          commitMessage
        });

        if ('status' in response && response.status === 'ok') {
          toast({
            description: 'Successfully saved survey',
            variant: 'success'
          })
        } else if ('reviewRequired' in response && response.reviewRequired) {
          // API returned that notification review is required
          setReviewRequired(response.result);
          setPublishing(false);
          return;
        } else {
          toast({
            description: JSON.stringify(response),
          })
        }
      } else {
        toast({
          description: <span>Everything's up to date!<br /><br />No changes to save.</span>,
        })
      }

      // Collab Mode should always attempt a publish if it's tried, even if the survey looks the same as the last published version. 
      // We may have missed other's changes, and do not want to overwrite those.
    } else if (collabEnabled) {
      // after publishing, make sure the collab source of truth is aligned with what we just published.
      const toSend = JSON.stringify(distroRef.current!.getPrunedEvents(stateRef.current!));
      const res = await writeNewCollabVersion({ toSend, publish: true, commitMessage });

      const collabResp = await getCollabState();
      newUnpublishedEvents = collabResp.unpublishedEvents;

      // This triggers a setSurvey that will set our survey state variable 
      // to the most up to date collab version.
      distroRef.current?.mergeEvents(collabResp.events);
      alertJustPublished();

      if (res.error !== '') {
        toast({
          description: res.error,
        })
      } else {
        toast({
          description: 'Successfully saved survey',
          variant: 'success'
        })
      }
    }

    stateRef.current!.unpublishedChanges = newUnpublishedEvents;
    const surveyResp = await loadSurvey({ name: distroConfigToSave, purpose: "final load after save" });
    setPublishing(false);
    setLoadedHash(surveyResp.hash);
    setLatestHash(surveyResp.hash);
    setLatestSurvey(surveyResp.config);
    setLastPublished(surveyResp.lastModified);
  }, [
    distroConfigName, collabEnabled, survey, tabId, config, canPublish, loadedHash
  ]);

  const isSafeToStartReview = collabEnabled || 
    (commitId 
      ? (!latestCommitMetadata || commitId === latestCommitMetadata.id) 
      : (latestHash === loadedHash && latestCommitMetadata?.reviewStatus !== "review_pending" && latestCommitMetadata?.reviewStatus !== "approved")
    );
  
  const startDistroReviewInternal = useCallback(async (reviewWindow?: Window | null) => {
    if (!canPublish || !survey) return;
    setPublishing(true);

    try {
      // Ensure we're passing an ExpandedSurvey object to the API
      let surveyToReview: ExpandedSurvey | undefined;
      let baseVersionId: string | undefined;
      let collabVersion: number | undefined;
      if (collabEnabled) {
        // Ensure that all Collab events currently in Dynamo are saved in S3 before
        // starting Review
        const snapshotCollab = await saveCollabDynamoEvents({ name: distroConfigName });

        if ('error' in snapshotCollab) {
          reviewWindow?.close(); // Close the window on error
          toast({
            description: 'Error starting review: ' + snapshotCollab.error,
            variant: 'error'
          });
          setPublishing(false);
          return;
        }

        baseVersionId = snapshotCollab.baseVersionId;
        collabVersion = snapshotCollab.collabVersion;
      } else {
        surveyToReview = Array.isArray(survey)
          ? { survey: survey, personas: [] }
          : survey
      }

      const response = await startDistroReviewReq({
        baseVersionId,
        collabVersion,
        survey: surveyToReview ? JSON.stringify(surveyToReview) : undefined,
        surveyName: distroConfigName,
        parentHash: loadedHash ||  undefined
      });

      if (response && response.reviewId) {
        // Navigate the already-opened window to the review page
        const reviewUrl = `/distro-review/${response.reviewId}`;
        if (reviewWindow && !reviewWindow.closed) {
          reviewWindow.location.href = reviewUrl;
        } else {
          // Fallback: if window was blocked/closed, try opening again
          window.open(reviewUrl, '_blank', 'noopener,noreferrer');
        }
        
        if (response.commitId && !collabEnabled) {
          const url = new URL(window.location.href);
          url.searchParams.set('commit', response.commitId);
          window.history.replaceState({}, '', url.toString());
          setCommitId(response.commitId);
        }
      } else {
        reviewWindow?.close();
        toast({
          description: 'Failed to start review process',
          variant: 'error'
        });
      }
    } catch (e) {
      console.error('Error starting review:', e);
      reviewWindow?.close();
      toast({
        description: 'Error starting review process',
        variant: 'error'
      });
    }

    setPublishing(false);
  }, [survey, canPublish, history, startDistroReviewReq, toast, distroConfigName, multipartUpload]);

  const handleStartDistroReview = useCallback(() => {
    if (!canPublish || !survey) return;
    if (!isSafeToStartReview) {
      setStartReviewConflictModalOpen(true);
      return;
    }
    
    // Open the window immediately to avoid popup blocking
    // Note: Not using 'noopener,noreferrer' features as some browsers return null with that parameter
    const reviewWindow = window.open('/redirect', '_blank');
    startDistroReviewInternal(reviewWindow);
  }, [canPublish, survey, isSafeToStartReview, startDistroReviewInternal]);

  const startReviewButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const callback = (e: KeyboardEvent) => {
      if (matchesExactKey(e, createPlatformShortcut('s'))) {
        e.preventDefault();
        startReviewButtonRef.current?.focus();
        setTimeout(() => startReviewButtonRef.current?.click(), 1);
      }
    }
    document.addEventListener('keydown', callback)
    return () => { document.removeEventListener('keydown', callback) }
  }, [canPublish, publishing, publish]);

  const [showSearch, setShowSearch] = useState(false);
  function doShowSearch() {
    setShowSearch(true);
  }

  async function rename() {
    let n = distroConfigName;
    let _n = prompt('Enter a new name');
    let doesExist;
    if (_n) {
      n = _n;
      doesExist = await doesSurveyExist({ name: n });

      // If does exist, we want to make sure to default to not saving
      let yesSave = !doesExist;
      if (doesExist) {
        yesSave = confirm(`A survey already exists with name: ${n}\nDo you wish to overwrite it?`);
      }

      if (yesSave) {
        setDistroConfigName(_n);
        setPublishing(true);
        const response = await saveSurvey({ name: n, content: JSON.stringify(survey || { survey: [], notifications: [], personas: [] }), browserTab: tabId });

        if ('status' in response && response.status === 'ok') {
          toast({
            description: `Successfully saved new survey: ${n}`,
            variant: 'success'
          })
        } else if ('reviewRequired' in response && response.reviewRequired) {
          // API returned that notification review is required
          setReviewRequired(response.result);
          setPublishing(false);
          return;
        } else {
          toast({
            description: JSON.stringify(response),
          })
        }

        setPublishing(false);
        window.location.pathname = window.location.pathname + n;
      }
    }
  }

  function injestTranslations() {
    if (translations) {
      importTranslations(survey, translations);
      console.log("Initializing distro with injestTranslations()", survey);
      distroRef.current?.initialize(survey, { ...user, name: config?.user?.name, tabId });
      alert("Injected translations, please save!");
    }
  }

  function openTranslationsModal() {
    if (collabEnabled) {
      toast({
        description: "You must disable collab to edit translations",
        variant: 'error'
      })
      return;
    }
    if (confirm('This will save the current state of your survey. Continue?')) {
      const extractedTranslations = extractDedupedTranslations(survey);
      console.log("Initializing distro with openTranslationsModal()", survey);
      distroRef.current?.initialize(survey, { ...user, name: config?.user?.name, tabId });
      // We need to save state after extracting so the ids persist.
      publish();
      setTranslations(extractedTranslations);
      initialTranslations.current = JSON.stringify(extractedTranslations);
      setTranslationModalOpen(true);
    }
  }

  function openContentTemplateModal() {
    if (confirm('This will save the current state of your survey. Continue?')) {
      // publish first. That makes sure that when we save content templates on the backend, 
      // no unpublished changes on the FE will be overwritten.
      publish();
      setContentTemplateModalOpen(true);
    }
  }

  function ConvertToObject() {
    setSurvey({
      survey: survey as any,
      notifications: [],
      personas: [],
    })
  }

  async function loadInfoFromApplicant() {
    const uid = prompt("Please enter a UID of an applicant");
    if (uid) {
      setMockInfo((await getEverything({ uid })).info)
    }
  }

  function CreateNew() {
    distroRef.current?.initialize({
      survey: [],
      personas: [],
    }, { ...user, name: config?.user?.name, tabId });
  }

  


  (window as any).magic = async (data: any) => {
    if (!data.question) {
      throw new Error('no question asked');
    }
    console.info('doing magic with data', data);
    // if (data.qualifier?.toLowerCase() === 'persona') {
    //   const prompt = data.question;
    //   const makeResult = (content: any) => ({ choices: [{ message: { content: JSON.stringify(content) } }] })

    //   if (isNaN(parseInt(prompt))) {
    //     return makeResult(await createPersona(10, prompt))
    //   }

    //   const persons = Array(parseInt(prompt)).fill(null).map(() => createPersona(10));

    //   return makeResult(
    //     (await Promise.allSettled(persons))
    //       .filter(r => r.status === 'fulfilled')
    //       .map((r: any) => r.value)
    //   );
    // }
    return await doMagic(data);
  }

  const incompatible = getDistroBrowserIncompatibility();
  if (incompatible) return incompatible;

  // This code enables the resizing of the left and right panes
  const [rightPaneWidth, setRightPaneWidth] = useState(33.3);
  const resizingRef = useRef(false);
  const onMouseDown = useCallback((e: MouseEvent) => {
    e.preventDefault()
    resizingRef.current = true;
  }, []);

  const onMouseUp = useCallback((e: MouseEvent) => {
    resizingRef.current = false;
  }, []);

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      if (!resizingRef.current) return;

      const container = e.currentTarget as HTMLElement;
      const newWidth = ((container.clientWidth - e.clientX) / container.clientWidth) * 100;

      setRightPaneWidth(newWidth);
    },
    [],
  );


  const [resetProgramModalOpen, setResetProgramModalOpen] = useState(false);

  async function reset() {
    setResetProgramModalOpen(true);
  }

  // These provide the children (e.g. ModularQuestionPage) with a ref to the scroll container
  // and a function to scroll to the top
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo(0, 0);
    }
  };

  const actions = useRef<ExtraJsx>();

  // This is pretty fast, ~ 15-50ms, but no need to recompute it
  // unless props.survey changes
  useEffect(() => {
    if (!survey) return;

    const expanded = expandTemplatesStrict(survey);
    let map: DependencyMap;
    try {
      map = getDependencyMap(expanded);
    } catch (e) {
      console.error('Error calculating dependencies', e);
      return;
    }
    actions.current = (action: ExtraJsxActions) => {
      if (action.action === 'COMPUTED') {
        const affectedNotifs = findAnyDownstreamNotifications(map, action.targetField);
        if (affectedNotifs.length > 0) {
          return (
            <div className='bg-[#fef3c6] rounded p-3'>
              <div>⚠️ There are notifications that depend on this computed field!</div>
              <ul>
                {(affectedNotifs || []).map(affectedNotif => (
                  <li id={affectedNotif.targetPrefix} key={affectedNotif.targetPrefix}>
                    <strong>{affectedNotif.name}</strong>  ({affectedNotif.targetPrefix})
                  </li>
                ))}
              </ul>
            </div>
          );
        }
      }
      return null;
    };
  }, [survey]);

  // We have to be a bit creative to make sure that the function has the right
  // context when it is called from the Distro (typesheets) side of things.
  const ExtraJsx: ExtraJsx = (action: ExtraJsxActions) => {
    if (actions.current) {
      return actions.current(action);
    }
    return null;
  }

  const deploymentName = useMemo(() => get_deployment(), []);

  return <>
    <UserInfoContext.Provider value={user}>
      <div className={"flex h-14 border-b border-blue-300 z-[60] relative top p-1 " + ((surveyChangedSinceLoaded && !collabEnabled) ? 'bg-red-200' : (process.env.NODE_ENV === 'development' ? 'bg-green-200' : 'bg-blue-200'))}>
        <div className="font-bold text-2xl text-blue-400 ml-3 mt-2">{`[${deploymentName}] Distro: `}
          <button onClick={() => rename()}
            disabled={publishing}
            className="border-0 bg-transparent mr-3 text-blue-600 hover:text-blue-800 font-normal">
            {distroConfigName || '[untitled]'}
          </button>
          {(!distroConfigName || !survey) && <button onClick={CreateNew}>Create New</button>}
        </div>
        {commitId && <div className="m-1 text-sm flex items-center h-full text-center">
          <button className="border-0 flex items-center bg-transparent ml-2 mr-3 text-blue-600 hover:text-gray-600 hover:line-through font-normal"
            aria-label="Reload without base commit" onClick={(e) => {
            //remove search params from url
              e.stopPropagation();
              const url = new URL(window.location.href);
              url.searchParams.delete('commit');
              window.history.replaceState({}, '', url.toString());
              window.location.reload();
            }}>
            <span>
              Base Commit: {commitId}
            </span>
          </button>
        </div>}
        <div className="flex-grow z-0 items-center justify-center px-2 py-1 space-x-4">
          <button className="bg-blue-50 border-green-200 rounded-md text-sm mt-2 p-1 hover:bg-blue-300" onClick={doShowSearch}>Search</button>
        </div>
        <div className="my-1" title={lastPublished ? `${distroConfigName} Last Published: ${new Date(lastPublished).toLocaleString()}` : ''}>
          {enablingCollab && <SpacedSpinner className='mr-2' />}
          {!collabEnabled && <button className={`border-0 rounded-md p-1 ${!enablingCollab && 'hover:bg-blue-300'} bg-blue-100 mr-2`} disabled={enablingCollab} onClick={doEnableCollab}>Enable Collab</button>}
          {collabEnabled && <button className="border-0 rounded-md hover:bg-blue-300 bg-blue-100 mr-2 p-1 " onClick={doDisableCollab}>Disable Collab</button>}
          {!collabEnabled && loadedHash && latestHash && loadedHash !== latestHash && "Loaded: " + loadedHash.slice(0, 8)}
          {!collabEnabled && loadedHash && latestHash && loadedHash !== latestHash && "Survey has changed! If you save you may overwrite someone else's work!"}
          <Dropdown
            color="blue" className="border-0 bg-transparent" colorIntensity={200}
            label={'Misc'} options={[
              { label: 'Load Info From Applicant', callback: () => loadInfoFromApplicant() },
              { label: 'Manage translations', callback: () => openTranslationsModal() },
              { label: 'Manage Content Templates', callback: () => openContentTemplateModal() },
              { label: 'Toggle Right Panel', callback: () => setShowRightPanel(!showRightPanel) },
              ...(publicConfiguration.experimental?.distroBreadcrumbs ? [{ label: 'Toggle Breadcrumb', callback: () => setShowBreadcrumb(!showBreadcrumb) }] : []),
              { label: 'Reset Program (Delete All Applicants)', callback: () => reset() },
              { label: 'View Distro Reviews', callback: () => window.open(`/distro-reviews`, '_blank') },
              { label: 'View Published History', callback: () => window.open(`/config-versions#${distroConfigName}`, '_blank') },
              ...(collabEnabled ? [{ label: 'View Auto-Saved History', callback: () => window.open(`/collab-versions#${distroConfigName}`, '_blank') }] : []),
              {
                label: 'Get Scope for User', callback: () => {
                  let user = window.prompt('Enter a user email');
                  if (!user) { alert("Must enter a user."); return; }
                  if (Array.isArray(survey) || !survey) { alert("Survey is not a whole survey"); return; }
                  let scope = CollectScopeForUser(EmailToUserId(user), (survey as ExpandedSurvey).survey, (survey as ExpandedSurvey).users);
                  console.log("scope for user", scope);
                  alert("Check the console.");
                }
              },
              { label: 'Enabled Surveys', callback: () => window.open(`/enabled-surveys`, '_blank') },
              ...(get_deployment().includes('postgres') || get_deployment().includes('demo') ? [{
                label: 'Force notifications immediately', callback: async () => alert(JSON.stringify(await sendNotifications({})))
              }] : [])
            ]} />
          {['entireprogram', 'audit'].includes(distroConfigName) ? <Dropdown
            color="blue" className="border-0 bg-transparent" colorIntensity={200}
            label={surveyName ? surveyName : "Surveys"} options={[
              ...(surveys && Array.isArray(surveys) ? surveys : []).filter(s => !!s).map((s) => ({
                label: s || '',
                callback: async () => {
                  let toLoad = s?.replace('dso-' + get_deployment() + '-', '')
                    .replace('.json', '');
                  window.location.href = '/config/' + toLoad;
                }
              }))
            ]} /> : null}
          <FacePile name={config?.user?.name} channel={get_deployment() + ":" + distroConfigName} browserTab={tabId} unsavedChanges={() => {
            if (collabEnabled) return false;
            return surveyChangedSinceLatest;
          }} />
        </div>
        <div className="my-[5px] ml-2">
          <DropdownMenu
            label={config?.user?.name || L.admin.your_account}
            align="end"
            options={[
              // Only exposing account settings if passwords are being used since we only have Security settings. We can open this up for all once there are more settings.
              ...(publicConfiguration.adminPasswords ? [{ label: "Account Settings", callback: () => { window.open('/admin/account_settings', '_blank', 'noopener,noreferrer'); } }] : []),
              { label: "Log out", callback: () => { history.push("/logout") } }
            ]}
          />
        </div>
        <>
          {latestCommitMetadata && latestCommitMetadata.reviewStatus === 'review_pending' &&
            <a href={`/distro-review/${latestCommitMetadata.reviewId}`} target="_blank" rel="noopener noreferrer" className="border-0 rounded-md text-sm font-medium text-center ml-3 my-1.5 p-1.5 bg-blue-100 text-blue-600 hover:text-blue-800 hover:no-underline hover:bg-blue-300">
              View Pending Review
            </a>
          }
          <button ref={startReviewButtonRef} 
            onClick={handleStartDistroReview} 
            disabled={publishing || !canPublish}
            className={'border-0 rounded-md ml-3 my-1.5 p-1.5 ' 
            + (!canPublish 
              ? ' bg-blue-100 text-gray-400 '
              : (!isSafeToStartReview
                ? ' bg-red-100 text-red-600 hover:text-red-800 hover:bg-red-300 ' 
                : ' bg-blue-100 text-blue-600 hover:text-blue-800 hover:bg-blue-300 '))
            + (surveyChangedSinceLatest ? ' outline outline-4 outline-orange-300 ' : '')}>
            {!publishing ? (
              <div className="relative inline-block">
                <span className="text-sm font-medium flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Start Review
                </span>
              </div>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
        </>
        <button
          onClick={publish as () => Promise<void>} 
          disabled={publishing || !canPublish}
          className={'border-0 rounded-md mx-3 my-1.5 bg-blue-100 p-1.5 ' + (canPublish ? 'text-blue-600 hover:text-blue-800 hover:bg-blue-300' : 'text-gray-400')
            + (surveyChangedSinceLatest ? ' outline outline-4 outline-orange-300 ' : '')}>
          {!publishing ? (
            <div className="relative inline-block">
              
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              
            </div>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
      </div>
      
      {showSearch && survey && <div className="z-100">
        <ConfigSearch survey={(survey as ExpandedSurvey).survey} close={() => setShowSearch(false)} />
      </div>
      }
      
      {roboscreenerValidationErrors &&
        <RoboscreenerSurveyErrorsModal
          errors={roboscreenerValidationErrors}
          closeModal={() => setRoboscreenerValidationErrors(undefined)}
        />
      }

      <div className="flex" style={{ height: 'calc(100vh - 6.5rem)' }} onMouseMove={onMouseMove} onMouseUp={onMouseUp}>
        {/* Distro editor */}
        <div className="flex-1 h-auto overflow-y-auto">
          <Suspense fallback={<h1 className="p-2">Loading Distro</h1>}>
            <Distro
              ref={distroRef}
              types='src/survey.ts'
              name='Root'
              migrations={MIGRATIONS}
              onChange={handleUpdates as (survey: Root, state: State, events: ChangeSet, expanded: any) => void}
              macros={MACROS}
              programConfig={publicConfiguration}
              getMacro={getMacro}
              macroEditor={props?.macroEditor}
              showBreadcrumb={showBreadcrumb}
              renderExtraJsx={ExtraJsx}
            />
          </Suspense>
        </div>
        {/* Resizing bar */}
        <div className="cursor-col-resize w-3" onMouseDown={onMouseDown}>
          <div className="w-1 bg-gray-100 m-auto h-full" />
        </div>
        {/* Preview/Right pane */}
        <div className="group relative h-auto" style={{ width: showRightPanel ? `${rightPaneWidth}%` : 0 }}>
          <SidebarToggle open={showRightPanel} side='right' dir={interfaceContext.direction || 'ltr'} width={rightPaneWidth} onClick={() => setShowRightPanel((showRightPanel) => !showRightPanel)} />
          {showRightPanel &&
          <div className={"overflow-y-auto flex flex-col h-full"}>
            {Array.isArray(survey) && <button onClick={ConvertToObject}>Convert to Object</button>}
            <div className="mb-2 bg-white shadow-sm">
              <button
                className={'border-b-2 border-l-0 border-r-0 border-t-0 bg-transparent inline-block w-auto m-1 p-0 ' + (selectedPreview === 'survey' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')}
                onClick={() => setSelectedPreview('survey')}
              >
                Survey
              </button>
              <button
                className={'border-b-2 border-l-0 border-r-0 border-t-0 inline-block bg-transparent w-auto m-1 p-0 ' + (selectedPreview === 'notifications' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')}
                onClick={() => setSelectedPreview('notifications')}
              >
                Notifications
              </button>
              <button
                className={'border-b-2 border-l-0 border-r-0 border-t-0 inline-block bg-transparent w-auto m-1 p-0 ' + (selectedPreview === 'payments' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')}
                onClick={() => setSelectedPreview('payments')}
              >
                Payments
              </button>
              <button
                className={'border-b-2 border-l-0 border-r-0 border-t-0 inline-block bg-transparent w-auto m-1 p-0 ' + (selectedPreview === 'targetFields' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')}
                onClick={() => setSelectedPreview('targetFields')}
              >
                Target Fields
              </button>
              <button
                className={'border-b-2 border-l-0 border-r-0 border-t-0 inline-block bg-transparent w-auto m-1 p-0 ' + (selectedPreview === 'dashboards' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')}
                onClick={() => setSelectedPreview('dashboards')}
              >
                Dashboards
              </button>
              <button
                className={'border-b-2 border-l-0 border-r-0 border-t-0 inline-block bg-transparent w-auto m-1 p-0 ' + (selectedPreview === 'testCases' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')}
                onClick={() => setSelectedPreview('testCases')}
              >
                TestCases
              </button>
              <button
                className={'border-b-2 border-l-0 border-r-0 border-t-0 inline-block bg-transparent w-auto m-1 p-0 ' + (selectedPreview === 'robonav' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')}
                onClick={() => setSelectedPreview('robonav')}
              >
                RoboNav
              </button>
              <button
                className={'border-b-2 border-l-0 border-r-0 border-t-0 inline-block bg-transparent w-auto m-1 p-0 ' + (selectedPreview === 'changes' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')}
                onClick={() => setSelectedPreview('changes')}
              >
                Changes
              </button>
              <button
                className={'border-b-2 border-l-0 border-r-0 border-t-0 inline-block bg-transparent w-auto m-1 p-0 ' + (selectedPreview === 'fieldMismatches' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')}
                onClick={() => setSelectedPreview('fieldMismatches')}
              >
                {'Field Mismatches {' + Object.keys(fieldMismatches).length + '}'}
              </button>
              <button
                className={'border-b-2 border-l-0 border-r-0 border-t-0 inline-block bg-transparent w-auto m-1 p-0 ' + (selectedPreview === 'validationSummary' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')}
                onClick={() => setSelectedPreview('validationSummary')}
              >
                {'Validation'}
              </button>
              <button
                className={'border-b-2 border-l-0 border-r-0 border-t-0 inline-block bg-transparent w-auto m-1 p-0 ' + (selectedPreview === 'ai-chat' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')}
                onClick={() => setSelectedPreview('ai-chat')}
              >
                {'AI Chat'}
              </button>
              {(!Array.isArray(survey) && survey?.config?.comms?.automaticMessageHandler) ? <button className={'border-b-2 border-l-0 border-r-0 border-t-0 inline-block bg-transparent w-auto m-1 p-0 ' + (selectedPreview === 'amh' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')}
                onClick={() => setSelectedPreview('amh')}
              >Automatic Message Handler</button> : null}
            </div>
            <div className={"h-full overflow-y-scroll"} ref={scrollContainerRef}>
              {(selectedPreview === 'targetFields' && survey) &&
              <TargetFieldExplorer survey={(survey as ExpandedSurvey).survey} mockInfo={mockInfo} setMockInfo={setMockInfo} />}

              {selectedPreview === 'notifications' && survey &&
              <NotificationsView survey={(survey as ExpandedSurvey)} enableEmailMarkdownGlobally={(survey as ExpandedSurvey).config?.experimental?.enableEmailMarkdownGlobally} />}

              {selectedPreview === 'payments' && survey &&
              <PaymentsEditor survey={(survey as ExpandedSurvey)} />}

              {selectedPreview === 'amh' && survey &&
              <AutomaticMessageHandler amh={Array.isArray(survey) ? {} as any : survey.config?.comms?.automaticMessageHandler} />}

              {selectedPreview === 'survey' &&
                <ScrollToTopContext.Provider value={{ scrollToTop, scrollContainerRef }}>
                  <SurveyPreview
                    survey={(survey as ExpandedSurvey)}
                    mockInfo={mockInfo}
                    setMockInfo={setMockInfo}
                    activeSurvey={activeSurvey}
                    setActiveSurvey={setActiveSurvey}
                    personas={personas}
                    subsurveys={subsurveys}
                    state={stateRef.current!}
                  />
                </ScrollToTopContext.Provider>
              }

              {selectedPreview == 'ai-chat' &&
                <ChatConversationProvider>
                  <DefaultChatSessionProvider conversationIdKey="distro_chat_conversation_id">
                    <CoachTab survey={survey!} state={stateRef.current!} />
                  </DefaultChatSessionProvider>
                </ChatConversationProvider>
              }

              {selectedPreview === 'dashboards' &&
              <DashboardDetail dashboards={Array.isArray(survey) ? {} as any : survey?.survey.filter(component => component.kind === 'Dashboard')} />
              }

              {selectedPreview === 'robonav' && survey &&
              <RoboNavConsole
                onPersonaDetailsLoaded={setMockInfo}
                surveys={Array.isArray(survey) ? [] : enumerateSubsurveys(survey.survey)}
                personas={Array.isArray(survey) ? [] : survey.personas}
              />
              }

              {selectedPreview === 'testCases' &&
              <TestCases survey={Array.isArray(survey) ? {} as any : survey} />}

              {selectedPreview === 'changes' &&
                <Changes stateRef={stateRef} collabEnabled={collabEnabled} surveyName={surveyName} />}

              {selectedPreview === 'fieldMismatches' &&
              <FieldMismatches fieldMismatches={fieldMismatches} survey={(survey as ExpandedSurvey).survey} />}

              {selectedPreview === 'validationSummary' &&
              <DistroValidation survey={survey} />}
            </div>
          </div>
          }
        </div>
      </div>
      <ResetProgramModal open={resetProgramModalOpen} onClose={() => setResetProgramModalOpen(false)} />
      <TranslationModal
        translations={translations}
        setTranslations={setTranslations}
        translationModalOpen={translationModalOpen}
        setTranslationModalOpen={setTranslationModalOpen}
        injestTranslations={injestTranslations}
        initialTranslations={initialTranslations} />
      {survey && <ContentTemplateModal
        open={contentTemplateModalOpen}
        onClose={() => setContentTemplateModalOpen(false)}
        survey={survey}
        reloadSurvey={async () => await reloadSurvey(true)}
      />}
      <StartReviewConflictModal
        isOpen={startReviewConflictModalOpen}
        onClose={() => setStartReviewConflictModalOpen(false)}
        onConfirm={() => {
          setStartReviewConflictModalOpen(false);
          // Open the window immediately to avoid popup blocking
          // Note: Not using 'noopener,noreferrer' features as some browsers return null with that parameter
          const reviewWindow = window.open('/redirect', '_blank');
          startDistroReviewInternal(reviewWindow);
        }}
        reviewId={latestCommitMetadata?.reviewId}
      />
      {distroValidationErrorsForModal &&
        <DistroValidationModal
          results={distroValidationErrorsForModal}
          onClose={({openValidationPanel, publishAnyway}) => {
            if (openValidationPanel) {
              setShowRightPanel(true);
              setSelectedPreview('validationSummary');
            }
            if (publishAnyway) {
              publish({skipDistroValidation: true});
            }
            setDistroValidationErrorsForModal(null);
          }}
        />
      }
      <ReviewRequiredModal
        isOpen={!!reviewRequired}
        onClose={() => setReviewRequired(null)}
        onProceedToReview={() => {
          setReviewRequired(null);
          // Open window immediately to avoid popup blocking
          const reviewWindow = window.open('/redirect', '_blank');
          startDistroReviewInternal(reviewWindow);
        }}
        reviewResult={reviewRequired}
      />
    </UserInfoContext.Provider>

  </>
}

/**
 * Displays a list of instances in the Distro config where for a given piece of content, the target fields in one language
 * do not match those in another language.
 */
function FieldMismatches(props: { fieldMismatches: FieldMismatchMap, survey: Survey }): JSX.Element {

  if (Object.keys(props.fieldMismatches).length === 0) {
    return <div>No mismatches 🎉</div>
  }

  return (
    <div>
      {Object.entries(props.fieldMismatches).map(([mismatchKey, mismatch]) =>
        <div key={mismatchKey} className='p-2'>
          <button className='border-0 rounded-md bg-transparent text-blue-600 hover:text-blue-800 hover:bg-blue-300' onClick={() => findAndGoToComponent(mismatch.tree, props.survey)}>{mismatchKey}</button>
          {Object.keys(mismatch.fieldsInLangMap).map((langKey) =>
            <div key={mismatchKey + '_fm_' + langKey} className='flex'>
              {/* Languages and their fields */}
              <div className='px-2 border-r-2 mr-2'>
                <div>
                  <strong>{supportedLanguages[langKey as keyof Text]}</strong>
                  {mismatch.fieldsInLangMap[langKey as keyof Text].map((field, index) =>
                    <div key={mismatchKey + '_f_' + langKey + '_' + field + '_' + index}
                      className='pl-2'>
                      <span style={field === '[Empty]' ? { backgroundColor: 'moccasin', fontWeight: 'bold' } : {}}>{field}</span>
                    </div>
                  )}
                </div>
              </div>
              {/* Languages and their content, with target fields highlighted */}
              <div>
                <br />
                {langKey !== '_id' && highlightSearchTerm(
                  (mismatch.text as any)[langKey],
                  mismatch.fieldsInLangMap[langKey as keyof Text].map(field => field.slice(1)),
                  { wholeMatchOnly: true, matchCase: true, includeDashboards: true, includeComments: false }
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
